import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  titleAs?: "div" | "h1" | "h2" | "p";
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  secondary?: React.ReactNode;
  layout?: "panel" | "banner";
  className?: string;
}

export function EmptyState({
  icon,
  title,
  titleAs = "div",
  description,
  action,
  secondaryAction,
  secondary,
  layout = "panel",
  className,
}: EmptyStateProps) {
  const TitleTag = titleAs;
  return (
    <div
      role="status"
      className={cn(
        layout === "panel"
          ? "flex flex-col items-center justify-center gap-3 rounded-panel border border-white/[0.06] bg-surface-raised px-6 py-12 text-center surface-toplight"
          : "flex w-full items-center gap-3 rounded-panel border border-white/[0.06] bg-surface-raised px-4 py-3 surface-toplight",
        className,
      )}
    >
      {icon && (
        <div className="text-muted-foreground/70 [&>svg]:size-8">{icon}</div>
      )}
      <div className={cn("space-y-1", layout === "banner" && "min-w-0 flex-1 text-left")}>
        <TitleTag className="text-sm font-semibold text-foreground">{title}</TitleTag>
        {description && (
          <div className="text-xs text-muted-foreground">{description}</div>
        )}
      </div>
      {action || secondaryAction ? (
        <div className="flex flex-col items-center gap-2 sm:flex-row">
          {action ? <EmptyStateActionButton action={action} /> : null}
          {secondaryAction ? (
            <EmptyStateActionButton action={secondaryAction} secondary />
          ) : null}
        </div>
      ) : null}
      {secondary}
    </div>
  );
}

function EmptyStateActionButton({
  action,
  secondary = false,
}: {
  action: EmptyStateAction;
  secondary?: boolean;
}) {
  const button = (
    <Button
      size="sm"
      variant={secondary ? "outline" : "default"}
      onClick={action.href ? undefined : action.onClick}
      className="min-h-11"
    >
      {action.label}
    </Button>
  );

  return action.href ? (
    <Button asChild size="sm" variant={secondary ? "outline" : "default"} className="min-h-11">
      <a href={action.href}>{action.label}</a>
    </Button>
  ) : button;
}
